import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import ErrorMessageDialog from '@pray/shared/components/ErrorMessageDialog/ErrorMessageDialog';
import { buttons, colors, typography } from '@pray/shared/components/foundations';
import Button from '@pray/shared/components/ui/Button';
import Text from '@pray/shared/components/ui/Text';
import productService from '@pray/shared/services/productService';
import type { IntervalPlan } from '@pray/shared/services/studioService/credits/types';
import { formatCurrency } from '@pray/shared/utils';
import eventTracking from '@pray/shared/utils/eventTracking';
import logger from '@pray/shared/utils/logger';
import { getAttributionSource } from '@pray/shared/utils/sourceAttribution';
import storage from '@pray/shared/utils/storage';

import CardPaymentForm from 'components/pages/components/checkout/CardPaymentForm/CardPaymentForm';
import WalletPaymentButton from 'components/pages/components/checkout/WalletPaymentButton/WalletPaymentButton';

import styles from './CheckoutCreditPlanForm.module.scss';

type CheckoutCreditPlanFormProps = {
  product: IntervalPlan;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  onSuccess?: () => void;
  onFailure?: () => void;
  onCancel?: () => void;
};

const intervalAbbr = {
  day: 'day',
  week: 'wk',
  month: 'mo',
  year: 'yr',
};

export default function CheckoutCreditPlanForm({
  product,
  form,
  onSuccess,
  onFailure,
  onCancel,
}: CheckoutCreditPlanFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [isProcessing, setIsProcessing] = useState(false);
  const [isAutoValidate, setIsAutoValidate] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  const checkout = {
    amount: product.price,
    interval: product.subscription_interval,
  };

  const dueDescription = product.is_free_trial_enabled ? 'Total due after free trial' : 'Total due today';

  function validateCheckoutForm() {
    const errors = {} as Record<string, string>;

    if (form.errors.card) errors.card = form.errors.card;
    if (!form.values.name) errors.name = 'Enter the full name as it is seen in your card';
    if (!form.values.card) errors.card = 'Enter the card number';

    form.setErrors(errors);

    return Object.keys(errors).length === 0;
  }

  useEffect(() => {
    if (!isAutoValidate) return;
    if (Object.keys(form.errors).length) validateCheckoutForm();
  }, [form.values, isAutoValidate]);

  async function handleWalletPayment({ token, recaptchaToken }) {
    try {
      setIsProcessing(true);

      await checkoutCreditPlan({
        token,
        recaptchaToken,
      });

      onSuccess?.();
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleCardPayment() {
    try {
      setIsProcessing(true);

      const isInvalid = !validateCheckoutForm();
      setIsAutoValidate(isInvalid);
      if (isInvalid) return;

      const cardElement = elements.getElement(CardElement);

      const { error, token } = await stripe.createToken(cardElement, {
        name: form.values.name,
      });

      // Handle Stripe token creation errors
      if (error) {
        logger.error('Failed to create Stripe token', error);
        const errorMessage =
          'There was an error processing your payment. Please check your card details and try again.';
        setCheckoutError(error.message || errorMessage);
        return;
      }

      const recaptchaToken = await executeRecaptcha('checkout');

      await checkoutCreditPlan({
        token,
        recaptchaToken,
      });

      onSuccess?.();
    } finally {
      setIsProcessing(false);
    }
  }

  async function checkoutCreditPlan({ token, recaptchaToken }) {
    try {
      const { id: userId } = storage.getUser();

      const purchaseData: Record<string, unknown> = {
        payment_token: token.id,
        recaptcha_token: recaptchaToken,
        artist_name: form.values.name,
      };

      const source = getAttributionSource('studio-credit-checkout');
      if (source) purchaseData.source = source;

      await productService.purchaseProductById({
        userId,
        productId: product.id,
        data: purchaseData,
      });

      handleEventTracking({
        product,
        userId,
        amount: checkout.amount,
      });

      setCheckoutError(null);
    } catch (err) {
      logger.error('Failed processing credit subscription checkout', err);
      handlePaymentFailure();
      throw err;
    }
  }

  function handlePaymentFailure() {
    setCheckoutError(
      'There was an error processing your payment. Please try again later or contact customer support for assistance.'
    );
    onFailure?.();
  }

  function handleEventTracking({ product, amount, userId }: { product: IntervalPlan; userId: string; amount: number }) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const eventData = {
      user_id: userId,
      sku: product.sku,
      amount,
    };

    /*
      These analytic events are used during PLG Onboarding
      In case we reuse this component for other flows, we should move them to onSuccess callback
    */
    eventTracking.trackEventCheckoutCompleted(
      amount,
      product.sku,
      product.id,
      false,
      false,
      undefined, // familyPlanDescription
      null, // familyPlanId
      undefined, // giftQuantity
      {}, // metricsProperties
      true // isCreditSubscriptionCheckout
    );
  }

  return (
    <div>
      <WalletPaymentButton
        amount={checkout.amount}
        planName={product.name}
        disabled={isProcessing}
        onCheckoutReady={handleWalletPayment}
        onCheckoutFailure={handlePaymentFailure}
      />

      <CardPaymentForm form={form} />

      <section className={styles.summarySection}>
        <div className={styles.summary}>
          <Text variant={typography.subhead_medium}>{dueDescription}</Text>
          <Text variant={typography.subhead_medium}>
            {formatCurrency(checkout.amount)}/{intervalAbbr[checkout.interval] || checkout.interval}
          </Text>
        </div>

        <div className={styles.submitButton}>
          <Button
            full
            loading={isProcessing}
            disabled={isProcessing}
            variant={buttons.variant.primary}
            size={buttons.size.large}
            color={colors.pray_featured}
            onClick={handleCardPayment}
          >
            Continue
          </Button>
        </div>
      </section>

      {!!checkoutError && (
        <ErrorMessageDialog
          title="Payment Failed"
          message={checkoutError}
          closeButtonText="Retry Payment"
          onClose={() => setCheckoutError(null)}
          onCancel={() => {
            setCheckoutError(null);
            onCancel?.();
          }}
        />
      )}
    </div>
  );
}
